import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminLogin() {
  const navigate = useNavigate();
  const [isSetup, setIsSetup] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    name: ''
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/admin/login`, {
        email: formData.email,
        password: formData.password
      });
      localStorage.setItem('admin_token', response.data.access_token);
      toast.success('Login successful!');
      navigate('/admin');
    } catch (error) {
      const message = error.response?.data?.detail || 'Login failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleSetup = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/admin/setup`, formData);
      toast.success('Admin account created! Please login.');
      setIsSetup(false);
      setFormData({ ...formData, name: '' });
    } catch (error) {
      const message = error.response?.data?.detail || 'Setup failed';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container" data-testid="admin-login-page">
      <motion.div 
        className="login-box"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h1 className="login-title" style={{ fontFamily: 'Playfair Display, serif' }}>
          {isSetup ? 'Admin Setup' : 'Admin Login'}
        </h1>

        {isSetup ? (
          <form onSubmit={handleSetup} data-testid="admin-setup-form">
            <div className="form-group">
              <label className="form-label">Name</label>
              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleChange}
                className="form-input"
                required
                data-testid="setup-name-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="form-input"
                required
                data-testid="setup-email-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Password</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="form-input"
                required
                minLength={6}
                data-testid="setup-password-input"
              />
            </div>
            <button 
              type="submit" 
              className="btn btn-primary" 
              style={{ width: '100%' }}
              disabled={loading}
              data-testid="setup-submit-btn"
            >
              {loading ? 'Creating...' : 'Create Admin Account'}
            </button>
            <p style={{ marginTop: '1rem', textAlign: 'center' }}>
              <button 
                type="button" 
                className="btn btn-ghost"
                onClick={() => setIsSetup(false)}
                data-testid="switch-to-login"
              >
                Back to Login
              </button>
            </p>
          </form>
        ) : (
          <form onSubmit={handleLogin} data-testid="admin-login-form">
            <div className="form-group">
              <label className="form-label">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                className="form-input"
                required
                data-testid="login-email-input"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Password</label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                className="form-input"
                required
                data-testid="login-password-input"
              />
            </div>
            <button 
              type="submit" 
              className="btn btn-primary" 
              style={{ width: '100%' }}
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
            <p style={{ marginTop: '1rem', textAlign: 'center', fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
              First time?{' '}
              <button 
                type="button" 
                className="btn btn-ghost"
                onClick={() => setIsSetup(true)}
                style={{ padding: '0', textDecoration: 'underline', color: 'var(--primary)' }}
                data-testid="switch-to-setup"
              >
                Create admin account
              </button>
            </p>
          </form>
        )}
      </motion.div>
    </div>
  );
}
